/**
 * The identifier a Client is addressed by.
 *
 * The Client Dashboard route carries the MAC, base64 encoded, and nothing
 * else identifies a client uniquely - a username can belong to several
 * connections at once. Canvas stores it under one of two keys depending on
 * whether the row came from the AP-scoped list or the global one.
 *
 * @param {object} row Canvas client row
 * @return {string|null} the client's MAC address
 */
export function clientMac(row) {
	const mac = row?.formatted_mac ?? row?.formatted_client_mac ?? row?.client_mac;
	const text = mac == null ? '' : String(mac).trim();
	return text || null;
}

/**
 * Normalize a Canvas client row into an agent candidate.
 *
 * Named by username where there is one, because that is what a person says.
 * The MAC is the identity underneath, and travels as the entity_id so the
 * dashboard can be reached however the client was named.
 *
 * @param {object} row Canvas client row
 * @return {object|null} agent candidate
 */
export function normalizeClient(row) {
	const mac = clientMac(row);
	if (!mac) return null;
	const name = String(row?.user_name || '').trim() || mac;
	// Only what the candidate schema declares. Everything else a request may
	// have named the client by - SSID, Access Point, band - travels in
	// field_values, which resolveByFields fills from the field catalogue the
	// lookup was sent with. That is the whole point of the catalogue: a
	// resource gains a searchable field by declaring one, not by inventing a
	// key here that the schema would refuse.
	return {
		entity_type: 'client',
		entity_id: mac,
		name,
		mac_address: mac,
		ip_address: optional(row?.ip_address),
		ipv6_address: optional(row?.ip6_address),
		site_name: optional(row?.ap_group_name),
	};
}

/**
 * @param {*} value stored value
 * @return {string|null} comparable text
 */
function optional(value) {
	if (value == null) return null;
	const text = String(value).trim();
	return text || null;
}
